import React, { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    ApolloClient,
    ApolloProvider,
    InMemoryCache,
    from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { HOST_URI } from "./constants/vars";
import { GlobalStyles } from "./constants/styles";
import { getAuthToken } from "./util/auth";
import { userActions } from "./store/userAuthSlice";
import { notificationsActions } from "./store/notificationsSlice";
import { getSubdomain } from "./util/subdomain";
import {BrandingProvider} from "./context/BrandingContext";


/**
 * ✅ Export a SINGLE Apollo client instance (stable) so Header can import it and call resetStore().
 */

// 1) Upload/http link
const httpLink = createUploadLink({
    uri: `${HOST_URI}/graphql`,
});

// 2) Auth + Org link (always re-reads token from storage)
const authLink = setContext((_, { headers }) => {
    const tokens = getAuthToken();

    const hostname = window.location.hostname;
    const search = window.location.search;

    const subdomain = getSubdomain(hostname, search);

    return {
        headers: {
            ...headers,
            authorization: tokens?.token ? `JWT ${tokens.token}` : "",
            ...(subdomain ? { "X-Org-Subdomain": subdomain } : {}),
        },
    };
});

// 3) Error link (no hooks here; use hard redirect)
const errorLink = onError(({ graphQLErrors }) => {
    if (!graphQLErrors?.length) return;

    const isPublicContentRoute = /^\/(events|schedule)(\/|$)/.test(window.location.pathname);
    if (isPublicContentRoute) return;

    for (const err of graphQLErrors) {
        if (err?.message === "You do not have permission to perform this action") {
            try {
                localStorage.removeItem("auth");
            } catch (e) {}

            window.location.replace("/");
            break;
        }
    }
});

// ✅ Single Apollo client instance
export const apolloClient = new ApolloClient({
    link: authLink.concat(from([errorLink, httpLink])),
    cache: new InMemoryCache(),
});

function App() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const authToken = getAuthToken();
    const token = authToken?.token || null;

    // Logout user, token expires after 30 minutes
    useEffect(() => {
        if (!token) return;

        const id = setTimeout(() => {
            dispatch(userActions.logout());
            dispatch(notificationsActions.resetNotifications());

            apolloClient.clearStore().catch(() => {});
            navigate("/", { replace: true });
        }, 30 * 60 * 1000);

        return () => clearTimeout(id);
    }, [token, dispatch, navigate]);

    return (
        <ApolloProvider client={apolloClient}>
            <BrandingProvider>
                {/* Google Fonts Import */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link
                    rel="preconnect"
                    href="https://fonts.gstatic.com"
                    crossOrigin="anonymous"
                />
                <link
                    href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600;1,700&display=swap"
                    rel="stylesheet"
                />

                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    closeOnClick
                    pauseOnHover
                    draggable
                    limit={3}
                    newestOnTop
                    theme="light"
                    toastClassName="kbola-toast"
                    bodyClassName="kbola-toast-body"
                    progressClassName="kbola-toast-progress"
                />

                <style>{`
                    .Toastify__toast-container { padding: 12px; }

                    .kbola-toast {
                      border-radius: 12px;
                      overflow: hidden;
                      border: 1px solid rgba(0,0,0,0.08);
                      background: #fff;
                      box-shadow: 0 18px 44px rgba(0,0,0,0.10);
                      padding: 12px 14px;
                      min-height: unset;
                      font-family: 'Poppins', sans-serif;
                    }

                    .kbola-toast-body {
                      margin: 0;
                      padding: 0;
                      color: ${GlobalStyles.colors.woodsmoke};
                      font-weight: 800;
                      font-size: 13.5px;
                      line-height: 1.25rem;
                    }

                    .kbola-toast-progress { background: ${GlobalStyles.colors.canary}; }
                    .Toastify__toast--success .kbola-toast-progress { background: ${GlobalStyles.colors.success}; }
                    .Toastify__toast--error .kbola-toast-progress { background: ${GlobalStyles.colors.danger}; }
                    .Toastify__toast--warning .kbola-toast-progress { background: ${GlobalStyles.colors.warning}; }
                    .Toastify__toast--info .kbola-toast-progress { background: ${GlobalStyles.colors.info}; }
                    .Toastify__toast--default .kbola-toast-progress { background: ${GlobalStyles.colors.canary}; }

                    .Toastify__close-button { opacity: 0.65; }
                    .Toastify__close-button:hover { opacity: 1; }

                    .Toastify__toast-icon { margin-inline-end: 10px; }
                `}</style>

                <Outlet />

            </BrandingProvider>
        </ApolloProvider>
    );
}

export default App;
